import { useNavigate, Navigate, Link } from 'react-router-dom';
import { HighlightButton, SecondaryButton } from '../HighlightButton/HighlightButton';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { useFileDownload } from '../../hooks/useFileDownload';
import { useCheckoutSession } from './hooks/useCheckoutSession';
import { ERROR_MESSAGES } from './utils/constants';
import styles from '../../css/Store.module.css';
import { utilsLog } from '../../utils/logUtils';

export const ReturnPage = () => {
  const navigate = useNavigate();
  const { status, customerEmail, purchasedProduct, sessionId } = useCheckoutSession();
  const { downloadError, downloadAllFiles, clearDownloadError } = useFileDownload();

  const DAYS_UNTIL_EXPIRY = 30;

  const handleBackToCatalog = () => {
    navigate('/work');
  };

  const handleDownloadAll = async () => {
    if (!purchasedProduct || !sessionId) return;

    try {
      await downloadAllFiles(purchasedProduct, sessionId);
    } catch (error) {
      utilsLog(`Download error: ${error}`, 'error');
    }
  };

  if (status === 'open') {
    return <Navigate to="/checkout" />;
  }

  if (status === 'error') {
    return (
      <main className={styles.pageContainer}>
        <div className={styles.errorContainer}>
          <h2>Verification Error</h2>
          <p className={styles.errorMessage}>
            {ERROR_MESSAGES.VERIFICATION_ERROR}
          </p>
          <p>If you&apos;ve completed your purchase, please check your email <strong>{customerEmail}</strong> for download links or contact our support team.</p>

          <div className={styles.errorActions}>
            <SecondaryButton onClick={handleBackToCatalog}>
              Back to Catalog
            </SecondaryButton>

            <SecondaryButton onClick={() => navigate('/contact')}>
              Contact Support
            </SecondaryButton>
          </div>
        </div>
      </main>
    );
  }

  if (status === 'complete') {
    return (
      <main className={styles.pageContainer}>
        <div className={styles.successContainer}>
          <div className={styles.downloadSection}>
            <p>Thank you for purchasing <strong>{purchasedProduct?.title || 'my music'}</strong>!</p>

            <HighlightButton onClick={handleDownloadAll} disabled={!purchasedProduct}>
              <div style={{ padding: 5, textTransform: 'uppercase' }}><strong>Download Files</strong></div>
            </HighlightButton>

            <p id="expiry" className={styles.expiryText}>This link will expire in {DAYS_UNTIL_EXPIRY} days.</p>

            {downloadError && (
              <div className={styles.errorMessage}>
                <p>{downloadError}</p>
                <SecondaryButton onClick={clearDownloadError}>Dismiss</SecondaryButton>
              </div>
            )}

            <p className={styles.emailSection}>An email with your download links has also been sent to <strong> {customerEmail}</strong>. Please check your spam or trash folder if you cannot find it. If you have any questions or issues with your download, please <Link to="/contact" aria-label="contact me (opens in new tab)" target="_blank" rel="noopener noreferrer">contact me</Link>.
            </p>
          </div>

          <div className={styles.helpSection}>
            <SecondaryButton onClick={handleBackToCatalog}>Back to Catalog</SecondaryButton>
          </div>
        </div>
      </main>
    );
  }

  // Loading state
  return (
    <main className={styles.pageContainer}>
      <LoadingSpinner message="Completing your purchase..." />
    </main>
  );
};

export default ReturnPage;
